import './SlaveGrid.css';

import {
  Dispatch,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router';
import Grid from '../../../Grid/Grid';
import DatasetButtons from '../../../DatasetButtons/DatasetButtons';
import FunctionalButtonsRow, {
  FunctionalButtonConfig,
} from '../../../FunctionalButtonsRow/FunctionalButtonsRow';
import { scrollGridToTop } from '../../../../../utils/managerUtils';
import ApiError from '../../../../ApiError/ApiError';
import {
  GlobalLocationStateType,
  GridRow,
  MatchMode,
} from '../../../../../types/types';
import { downloadOutline, searchOutline, refreshOutline } from 'ionicons/icons';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import useRoutes from '../../../../../hooks/useRoutes';
import { useSelectedRowsContext } from '../../../../../hooks/context/useSelectedRowsContext';
import { useDatasetContext } from '../../../../../hooks/context/useDatasetContext';
import { useSlaveDatasetContext } from '../../../../../hooks/context/useSlaveDatasetContext';
import { useScrollGridOnCachedQueryChange } from '../../../../../hooks/useScrollGridOnCachedQueryChange';
import RequestDataProvider from '../../../../../context/RequestDataContext';
import { getDataKey } from '../../../../../utils/getDataKey';
import SeptimLoadingSpinner from '../../../SeptimLoadingSpinner/SeptimLoadingSpinner';
import { getDatasetButtonsWrapperInlineStyle } from '../../../../../utils/getDatasetButtonsWrapperInlineStyle';
import { GridColumn } from '../../../Grid/types';
import { selectGridRows } from '../../../../../utils/dataset/selectGridRows';
import { useInlineDatasetEdit } from './hooks/useInlineDatasetEdit';

interface SlaveGridProps {
  subsetId: string;
  slaveDatasets: any;
  setShowSlaveFilter?: (value: any) => void;
  slaveLazyState: any;
  setSlaveLazyStates: (value: any) => void;
  slaveFilter: any;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  openFullscreenReference: (
    column: GridColumn,
    value: string | null,
    matchMode: MatchMode,
    label: string,
    datasetId: string,
    resolvedFilter?: Record<string, any> | null
  ) => void;
  onLoadingChange?: (loading: boolean) => void;
  // Quick-filter shortcut scope provided by SlaveTabs (the whole slave card).
  shortcutScopeRef?: RefObject<HTMLElement | null>;
}

const SlaveGrid = ({
  subsetId,
  slaveDatasets,
  setShowSlaveFilter,
  slaveLazyState,
  setSlaveLazyStates,
  slaveFilter,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
  onLoadingChange,
  shortcutScopeRef,
}: SlaveGridProps) => {
  const [datasetMinWidth, setDatasetMinWidth] = useState(0);
  const [functionalHiddenState, setFunctionalHiddenState] = useState({
    hidden: 0,
    totalHideable: 0,
  });

  const gridRef = useRef<HTMLDivElement | any>(null);
  const lookupAttemptedRef = useRef<boolean>(false);
  const headerRowRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslate();
  const { state, pathname } = useLocation<GlobalLocationStateType>();
  const history = useHistory();
  const routes = useRoutes();
  const {
    dataset: rootDataset,
    patchRowLocally: patchMasterRowLocally,
  } = useDatasetContext();
  const {
    datasetError,
    dataError,
    filterError,
    datasetLoading,
    dataset,
    data,
    gridLoading,
    activeFilters,
    activeSorting,
    datasetData,
    expandedRow,
    lazyLoading,
    disabledLazyLoading,
    columnsWidth,
    cachedQueryId,
    dataKey,
    requestData,
    fetchDatasetData,
    patchRowLocally: patchSlaveRowLocally,
    acknowledgeParentRowPatch,
    setExpandedRow,
    loadMoreData,
    locateAndFetchById,
  } = useSlaveDatasetContext();
  const {
    selectedSlaveRows,
    selectAllSlaveChecked,
    selectedSlaveVirtualRows,
    deselectedSlaveRowIds,
    expandedRow: masterExpandedRow,
    setExpandedRow: setMasterExpandedRow,
    processSelectedSlaveRowsChange,
    processSelectAllSlaveChange,
    resetSlaveSelectedRows,
    slaveReloadLocateScrollTargetRef,
    slaveReloadLocateInProgress,
  } = useSelectedRowsContext();
  const gridData = useMemo(
    () => selectGridRows(data, dataset),
    [data, dataset]
  );
  const masterDataKey = getDataKey(rootDataset);
  const applyMasterPatch = useCallback(
    (patch: GridRow): void => {
      const id = String(patch[masterDataKey] ?? '');
      if (!id) return;
      acknowledgeParentRowPatch(patch);
      patchMasterRowLocally(id, patch);
      setMasterExpandedRow((current) =>
        current && String(current[masterDataKey]) === id
          ? { ...current, ...patch }
          : current
      );
    },
    [
      acknowledgeParentRowPatch,
      masterDataKey,
      patchMasterRowLocally,
      setMasterExpandedRow,
    ]
  );
  const inlineEdit = useInlineDatasetEdit(dataset, {
    onRowPatch: patchSlaveRowLocally,
    onMasterPatch: applyMasterPatch,
  });

  // Scroll to top when X-CACHEDQUERY-ID changes on lazy load
  useScrollGridOnCachedQueryChange(
    datasetData,
    subsetId,
    cachedQueryId,
    gridRef
  );

  const handleReloadData = () => {
    scrollGridToTop(gridRef);
    fetchDatasetData(dataset, false);
    setExpandedRow(null);
  };

  const findRowCleanup = () => {
    history.replace(pathname, {
      ...state,
      subsetFromForms: undefined,
      subsetItemId: undefined,
    });
  };

  const handleFindRowInDataset = useCallback(
    async (itemId: string, forceNewData: boolean = false) => {
      const resolvedData = data ? data : [];

      if (!forceNewData) {
        // 1) Search current data — if the row is here, expand + scroll, done.
        const foundIndex = resolvedData.findIndex(
          (row: any) => String(row[dataKey]) === itemId
        );

        if (foundIndex !== -1) {
          findRowCleanup();
          setExpandedRow(resolvedData[foundIndex]);
          return;
        }
      }

      // 2) Not in current page: run one probe/refetch. On success, updated `data`
      // reruns this effect and branch (1) expands the row. Ref guard prevents duplicates.
      if (lookupAttemptedRef.current) return;
      lookupAttemptedRef.current = true;

      if (state?.subsetSubmitted !== undefined) {
        history.replace(pathname, {
          ...state,
          subsetSubmitted: state?.subsetSubmitted
            ? undefined
            : state?.subsetSubmitted,
        });
      }

      await locateAndFetchById(
        itemId,
        forceNewData,
        () => {
          findRowCleanup();
          setExpandedRow(null);
        },
        () => {
          findRowCleanup();
          history.replace(pathname, {
            ...state,
            submitted: undefined,
            fromForms: undefined,
            subsetItemId: undefined,
            subsetFromForms: undefined,
            subsetSubmitted: undefined,
          });
        }
      );
    },
    [data, state]
  );

  const filterVisible = Boolean(
    setShowSlaveFilter &&
    slaveDatasets[subsetId] &&
    slaveLazyState &&
    dataset?.filterId
  );

  const datasetLocked =
    functionalHiddenState.totalHideable > 0 && functionalHiddenState.hidden > 0;

  const masterIdItem = getDataKey(rootDataset);
  const masterId = masterExpandedRow
    ? String(masterExpandedRow[masterIdItem] ?? '')
    : '';

  const handleOpenExport = useCallback((): void => {
    if (!rootDataset?.datasetId || !masterId) return;
    // Subset export behaves as a main-record export, but uses the slave grid selection.
    history.push(routes.datasetExport(rootDataset.datasetId, 'main'), {
      exportSelection: {
        selectedRows: selectedSlaveRows,
        selectedVirtualRows: selectedSlaveVirtualRows,
        selectAllChecked: selectAllSlaveChecked,
        deselectedRowIds: Array.from(deselectedSlaveRowIds),
      },
      subsetExport: {
        subsetId,
        parentRow: masterExpandedRow,
        parentId: masterId,
        parentIdItem: masterIdItem,
        activeFilters,
        activeSorting,
        rows: data,
      },
    });
  }, [
    rootDataset,
    masterId,
    routes,
    history,
    selectedSlaveRows,
    selectedSlaveVirtualRows,
    selectAllSlaveChecked,
    deselectedSlaveRowIds,
    subsetId,
    masterExpandedRow,
    masterIdItem,
    activeFilters,
    activeSorting,
    data,
  ]);

  const disabledExport =
    (gridLoading && !lazyLoading) ||
    !data ||
    data.length === 0 ||
    !rootDataset?.datasetId ||
    !masterId;

  const functionalButtons: FunctionalButtonConfig[] = useMemo(() => {
    const buttons: FunctionalButtonConfig[] = [
      {
        key: 'export',
        label: t('export.title'),
        icon: downloadOutline,
        onClick: handleOpenExport,
        disabled: disabledExport,
        hidePriority: 2,
        className: 'header-btn',
      },
      {
        key: 'reload',
        label: t('reload'),
        icon: refreshOutline,
        onClick: handleReloadData,
        disabled: gridLoading && !lazyLoading,
        hidePriority: 1,
        className: 'header-btn',
      },
    ];
    if (filterVisible && setShowSlaveFilter) {
      buttons.push({
        key: 'filter',
        label: t('detailedFilter'),
        icon: searchOutline,
        onClick: () => setShowSlaveFilter(subsetId),
        disabled: gridLoading && !lazyLoading,
        alwaysVisible: true,
        className: 'header-btn',
      });
    }
    return buttons;
  }, [
    t,
    gridLoading,
    lazyLoading,
    filterVisible,
    setShowSlaveFilter,
    subsetId,
    handleReloadData,
    handleOpenExport,
    disabledExport,
  ]);

  // Scroll to top when X-CACHEDQUERY-ID changes on lazy load
  useScrollGridOnCachedQueryChange(
    datasetData,
    subsetId,
    cachedQueryId,
    gridRef
  );

  useEffect(() => {
    const baseOmitCondition =
      datasetLoading ||
      !dataset ||
      !data ||
      !state?.subsetItemId ||
      (gridLoading && !lazyLoading);

    if (baseOmitCondition) return;

    if (!state.subsetFromForms) return;

    handleFindRowInDataset(state.subsetItemId ?? '', state.subsetSubmitted);
  }, [data, state?.subsetFromForms]);

  useEffect(() => {
    lookupAttemptedRef.current = false;
  }, [pathname, activeFilters, activeSorting]);

  useEffect(() => {
    scrollGridToTop(gridRef);
  }, [activeFilters, activeSorting]);
  const enabledVirtualScroller =
    !gridData || gridLoading || gridData.length >= 20;

  const hasError = Boolean(
    (datasetError && datasetError.datasetId === subsetId) ||
    (dataError && dataError.datasetId === subsetId) ||
    (filterError && filterError.datasetId === subsetId)
  );

  // Also guard on dataset.datasetId !== subsetId so that when a freshly-
  // mounted SlaveGrid (e.g. after switching tabs in a newly-expanded row)
  // briefly reads stale SlaveDatasetContext values from the previous subset,
  // we show the outer spinner instead of flashing the old dataset's rows.
  const isLoading =
    !hasError &&
    (datasetLoading || !dataset || dataset.datasetId !== subsetId || !data);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  if (hasError) {
    const error = datasetError || dataError || filterError;
    return <ApiError error={error?.error} />;
  }

  if (isLoading) {
    return <SeptimLoadingSpinner className="slave-tabs-loading-spinner" />;
  }

  const datasetWrapperStyle = getDatasetButtonsWrapperInlineStyle(
    datasetMinWidth,
    datasetLocked
  );

  return (
    <RequestDataProvider requestData={requestData}>
      <div className="header slave-grid-header" ref={headerRowRef}>
        <div className="dataset-buttons-wrapper" style={datasetWrapperStyle}>
          <DatasetButtons
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            data={data}
            dataset={dataset}
            datasetData={datasetData}
            selectedVirtualRows={selectedSlaveVirtualRows}
            selectedRows={selectedSlaveRows}
            currentRow={expandedRow}
            masterRow={masterExpandedRow}
            refetchData={() => fetchDatasetData(dataset)}
            onOverflowChange={(overflowState) =>
              setDatasetMinWidth((previous) =>
                previous === overflowState.collapsedWidth
                  ? previous
                  : overflowState.collapsedWidth
              )
            }
          />
        </div>

        <FunctionalButtonsRow
          buttons={functionalButtons}
          headerRowRef={headerRowRef}
          popoverLabel={t('display')}
          onHiddenChange={(nextState) =>
            setFunctionalHiddenState((previous) =>
              previous.hidden === nextState.hidden &&
              previous.totalHideable === nextState.totalHideable
                ? previous
                : nextState
            )
          }
        />
      </div>
      <Grid
        dataset={dataset}
        filter={slaveFilter}
        data={gridData}
        dataLoading={
          (gridLoading && !lazyLoading) || slaveReloadLocateInProgress
        }
        selectedRows={selectedSlaveRows}
        selectAllChecked={selectAllSlaveChecked}
        processSelectAllChange={(checkboxEvent) =>
          processSelectAllSlaveChange(
            checkboxEvent,
            // dataRef.current,
            gridData ?? [],
            datasetData
          )
        }
        processSelectedRowsChange={(checkboxEvent) => {
          processSelectedSlaveRowsChange(
            checkboxEvent,
            // dataRef.current,
            gridData ?? [],
            datasetData,
            dataKey,
            selectedSlaveVirtualRows
          );
        }}
        resetSelectedRows={resetSlaveSelectedRows}
        lazyState={slaveLazyState}
        setLazyState={(newLazyState) =>
          setSlaveLazyStates((v) => ({
            ...v,
            [subsetId]: newLazyState,
          }))
        }
        expandedRow={expandedRow}
        setExpandedRow={setExpandedRow}
        dataKey={getDataKey(dataset)}
        gridRef={gridRef}
        shortcutScopeRef={shortcutScopeRef}
        loadMoreRows={loadMoreData}
        disabledLazyLoading={disabledLazyLoading || slaveReloadLocateInProgress}
        scrollTargetOverrideRef={slaveReloadLocateScrollTargetRef}
        columnsWidth={columnsWidth}
        // Disable virtual scroller if there are not enough rows for scrolling
        enabledVirtualScroller={enabledVirtualScroller}
        emptyMessageRootSelector=".p-datatable"
        referenceDatasets={referenceDatasets}
        setReferenceDatasets={setReferenceDatasets}
        isSlaveGrid
        inlineEdit={{ masterRow: masterExpandedRow, onSave: inlineEdit.save }}
        openFullscreenReference={(
          column,
          value,
          matchMode,
          label,
          resolvedFilter
        ) =>
          openFullscreenReference(
            column,
            value,
            matchMode,
            label,
            subsetId,
            resolvedFilter
          )
        }
      />
    </RequestDataProvider>
  );
};

export default SlaveGrid;
